import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { AdminLayout } from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { k: "percent", label: "Percentage Off (%)" },
  { k: "dollar", label: "Dollar Off ($)" },
  { k: "free_delivery", label: "Free Delivery" },
];
const empty = { code: "", discount_type: "percent", value: 10, expires_at: "", usage_limit: "", active: true };

export default function Promos() {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = () => { setLoading(true); api.get("/admin/promos").then((r) => setPromos(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const body = { ...editing, value: Number(editing.value || 0), usage_limit: editing.usage_limit ? Number(editing.usage_limit) : null, expires_at: editing.expires_at || null };
    try {
      if (editing.id) await api.put(`/admin/promos/${editing.id}`, body);
      else await api.post("/admin/promos", body);
      toast.success("Promo saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const remove = async (id) => { if (!window.confirm("Delete promo?")) return; await api.delete(`/admin/promos/${id}`); load(); };

  const field = "bg-input border-gold/25 text-cream rounded-xl";
  const desc = (p) => p.discount_type === "percent" ? `${p.value}% off` : p.discount_type === "dollar" ? `$${p.value} off` : "Free delivery";

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-3xl text-cream">Promo Codes</h1>
        <Button className="btn-gold rounded-full font-semibold" onClick={() => setEditing({ ...empty })} data-testid="add-promo-btn"><Plus size={16} className="mr-1" /> New Code</Button>
      </div>

      {loading ? <div className="flex justify-center py-20"><Loader2 className="animate-spin text-gold" size={36} /></div> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {promos.map((p) => (
            <div key={p.id} className="gold-border-card rounded-xl bg-card-surface p-5" data-testid={`promo-${p.code}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono text-gold text-lg font-bold">{p.code}</span>
                <button onClick={() => remove(p.id)} className="text-cream/40 hover:text-destructive"><Trash2 size={15} /></button>
              </div>
              <p className="text-cream mt-1">{desc(p)}</p>
              <p className="text-cream/50 text-xs mt-2">Used {p.used || 0}{p.usage_limit ? ` / ${p.usage_limit}` : ""} · {p.active ? "Active" : "Inactive"}{p.expires_at ? ` · Expires ${p.expires_at.slice(0, 10)}` : ""}</p>
              <Button size="sm" variant="outline" className="w-full mt-3 bg-transparent border-gold/40 text-cream hover:bg-gold/10 rounded-lg" onClick={() => setEditing({ ...p, expires_at: p.expires_at ? p.expires_at.slice(0, 10) : "", usage_limit: p.usage_limit || "" })} data-testid={`edit-promo-${p.code}`}>Edit</Button>
            </div>
          ))}
          {promos.length === 0 && <p className="text-cream/40">No promo codes yet. Try creating WELCOME10, CARMAX, or WEEKEND.</p>}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-card-surface border-gold/30 text-cream max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-gold text-2xl">{editing?.id ? "Edit" : "New"} Promo Code</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div><Label className="text-cream/70 text-sm">Code</Label><Input className={`${field} font-mono uppercase`} value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })} data-testid="promo-code-field" /></div>
              <div><Label className="text-cream/70 text-sm">Discount Type</Label>
                <select className={`w-full ${field} h-11 px-3`} value={editing.discount_type} onChange={(e) => setEditing({ ...editing, discount_type: e.target.value })} data-testid="promo-type">
                  {TYPES.map((t) => <option key={t.k} value={t.k}>{t.label}</option>)}
                </select>
              </div>
              {editing.discount_type !== "free_delivery" && (
                <div><Label className="text-cream/70 text-sm">Value</Label><Input type="number" className={field} value={editing.value} onChange={(e) => setEditing({ ...editing, value: e.target.value })} data-testid="promo-value" /></div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-cream/70 text-sm">Expires (optional)</Label><Input type="date" className={field} value={editing.expires_at} onChange={(e) => setEditing({ ...editing, expires_at: e.target.value })} data-testid="promo-expires" /></div>
                <div><Label className="text-cream/70 text-sm">Usage Limit</Label><Input type="number" className={field} value={editing.usage_limit} onChange={(e) => setEditing({ ...editing, usage_limit: e.target.value })} data-testid="promo-limit" /></div>
              </div>
              <label className="flex items-center gap-2 text-sm text-cream/80"><input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} data-testid="promo-active" /> Active</label>
              <Button className="btn-gold w-full rounded-full py-6 font-semibold" onClick={save} data-testid="save-promo-btn">Save Promo</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
